import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Building2, CreditCard, Shield, Clock, ChevronRight, ArrowRight, CheckCircle2, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const features = [
    {
      icon: Building2,
      title: "Location-Tagged Payouts",
      description: "Every invoice or direct deposit can be tagged to a clinic location or cost center so the practice can track payments by site."
    },
    {
      icon: CreditCard,
      title: "Multi-Rail Payments",
      description: "Same-Day ACH, RTP / FedNow, and Wire — vendors and employees get paid the fastest way their bank supports."
    },
    {
      icon: Shield,
      title: "Plaid Bank-Grade Security",
      description: "Bank verification via Plaid Link with same-day micro-deposits. No routing/account numbers ever leave your browser."
    },
    {
      icon: Clock,
      title: "Direct Deposit & Audit Trail",
      description: "Employees receive PPD direct deposits; vendors receive WEB-authorized credits. Every penny is NACHA-compliant and audit-logged."
    }
  ];

  const benefits = [
    "Bank verification in seconds via Plaid Link",
    "Same-Day ACH, RTP / FedNow, and Wire support",
    "NACHA-compliant SEC codes & company entry descriptions",
    "Project-tagged payouts for clean job costing",
    "Full audit trail of every payment",
    "Bank-grade encryption end-to-end"
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navigation */}
      <nav className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img src="/cpc-logo.png" alt="Comprehensive Pain Consultants" className="h-9 w-auto rounded-lg bg-emerald-900 px-2.5 py-1.5" />
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-stone-600 hover:text-emerald-900" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-6" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4" />
                Plaid-Powered Security
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Get Paid by{" "}
                <span className="text-gradient">CPC</span>{" "}
                — Securely, On Time
              </h1>
              <p className="text-lg text-stone-600 max-w-xl">
                CPC Pay is where Comprehensive Pain Consultants' vendors and employees sign up,
                link their bank, and receive verified payments. Bank-grade Plaid security, same-day ACH,
                RTP and wire support, with full NACHA compliance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-8 h-12 text-base" data-testid="hero-get-started-btn">
                    Start Free Today
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-stone-300" data-testid="hero-login-btn">
                    I Have an Account
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-stone-200 border-2 border-white flex items-center justify-center text-xs font-medium text-stone-600">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-stone-600">
                  <span className="font-semibold text-stone-900">CPC</span> · Pain management · Asheville · Marion · Waynesville · Murphy, NC
                </p>
              </div>
            </div>
            <div className="relative animate-fadeIn animation-delay-200">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Doctor consulting a patient at a pain clinic"
                  className="rounded-2xl shadow-2xl w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 z-20 animate-slideIn animation-delay-400">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-900">Payment Received</p>
                    <p className="text-xs text-stone-500">$1,850.00 • Just now</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-sky-400 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-emerald-500 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything You Need
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              A purpose-built payments portal for CPC — every vendor and employee gets paid the right way, on the right rail, with a complete audit trail.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="card-hover border-stone-100 bg-white"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-emerald-900" />
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-stone-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1764727291644-5dcb0b1a0375?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Modern medical clinic reception"
                className="rounded-2xl shadow-lg w-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Built for a Medical Practice
              </h2>
              <p className="text-stone-600">
                CPC Pay is shaped around how a pain-management practice actually pays its people:
                suppliers and contractors get vendor credits tagged to a location, employees receive
                direct deposits as PPD, and every transaction is NACHA-compliant by default.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-emerald-700" />
                    </div>
                    <span className="text-stone-700">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-8" data-testid="benefits-cta-btn">
                  Create Free Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Get Paid the Right Way?
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Whether you're a vendor or an employee of CPC, sign up in minutes and start receiving verified Plaid-secured payments.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-emerald-900 hover:bg-stone-100 rounded-full px-8 h-12" data-testid="cta-vendor-btn">
                I'm a Vendor
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-800 rounded-full px-8 h-12" data-testid="cta-employee-btn">
                I'm an Employee
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-stone-900 text-stone-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src="/cpc-logo.png" alt="Comprehensive Pain Consultants" className="h-8 w-auto" />
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="text-sm text-stone-400 hover:text-emerald-400 transition-colors"
                data-testid="footer-privacy-policy-link"
              >
                Privacy Policy
              </Link>
              <Link
                to="/plaid-privacy"
                className="text-sm text-stone-400 hover:text-emerald-400 transition-colors"
                data-testid="footer-plaid-privacy-policy-link"
              >
                Plaid Privacy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-stone-400 hover:text-emerald-400 transition-colors"
                data-testid="footer-terms-policy-link"
              >
                Terms
              </Link>
              <a
                href="https://www.cpcdoctors.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-stone-400 hover:text-emerald-400 transition-colors"
                data-testid="footer-website-link"
              >
                <Building2 className="w-4 h-4" />
                cpcdoctors.com
              </a>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} Comprehensive Pain Consultants. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
